import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getManifestPrintData } from "@/lib/manifests.functions";
import { Button } from "@/components/ui/button";
import { Barcode } from "@/components/barcode";
import { Printer, ArrowLeft } from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/app/manifests/$manifestId/print",
)({ component: ManifestPrintPage });

function addressLine(a: any): string {
  if (!a) return "";
  if (typeof a === "string") return a;
  return [a.street1, a.street2, [a.city, a.state, a.postal_code].filter(Boolean).join(", "), a.country]
    .filter(Boolean).join("\n");
}

function ManifestPrintPage() {
  const { manifestId } = Route.useParams();
  const fetchFn = useServerFn(getManifestPrintData);
  const { data, isLoading, error } = useQuery({
    queryKey: ["manifest-print", manifestId],
    queryFn: () => fetchFn({ data: { manifestId } }),
  });

  useEffect(() => {
    document.title = data?.manifest?.manifest_number
      ? `Manifest ${data.manifest.manifest_number}`
      : "Carrier Manifest";
  }, [data]);

  if (isLoading) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  if (error) return <div className="p-10 text-sm text-destructive">{(error as Error).message}</div>;
  if (!data) return null;

  const m: any = data.manifest;
  const rows: any[] = data.rows ?? [];
  const totals = data.totals ?? { shipments: 0, packages: 0, pallets: 0, weight_lb: 0 };
  const manifestNo = m.manifest_number ?? m.id.slice(0, 8);

  return (
    <div className="min-h-screen bg-muted/40">
      <div className="print:hidden border-b bg-card">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" asChild>
            <Link to="/app/admin/manifests"><ArrowLeft className="h-4 w-4 mr-2" />Back to manifests</Link>
          </Button>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground uppercase">
              {m.status} · {m.carrier?.toUpperCase()} · {m.manifest_date}
            </span>
            <Button onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-2" />Print / Save PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6 print:p-0">
        <div className="bg-white text-black p-10 shadow-sm print:shadow-none print:p-4 text-[11px] leading-tight">
          {/* Title */}
          <div className="flex items-start justify-between border-b-2 border-black pb-3 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-600">End-of-Day Carrier Manifest</div>
              <div className="text-2xl font-bold mt-1">{m.carrier?.toUpperCase()} Manifest</div>
              <div className="text-xs text-neutral-700 mt-1">Date {m.manifest_date} · Status {m.status}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase text-neutral-600">Manifest #</div>
              <div className="text-xl font-mono font-bold">{manifestNo}</div>
              <div className="mt-2">
                <Barcode value={manifestNo} format="CODE128" height={36} width={1.4} displayValue={false} margin={0} />
              </div>
            </div>
          </div>

          {/* Origin facility + totals */}
          <div className="grid grid-cols-2 gap-0 border border-black mb-3">
            <div className="p-2 border-r border-black">
              <div className="text-[10px] uppercase tracking-wider text-neutral-600">Origin</div>
              <div className="font-semibold">{data.facility?.name ?? data.tenant?.name ?? "—"}</div>
              <div className="whitespace-pre-line text-[11px]">{addressLine(data.facility?.address) || "—"}</div>
            </div>
            <div className="p-2 grid grid-cols-4 gap-2 text-center">
              <Total label="Shipments" value={totals.shipments} />
              <Total label="Packages" value={totals.packages} />
              <Total label="Pallets" value={totals.pallets} />
              <Total label="Weight (lb)" value={totals.weight_lb} />
            </div>
          </div>

          {/* Shipment table */}
          <table className="w-full border-collapse border border-black">
            <thead>
              <tr className="bg-neutral-100 text-[10px] uppercase">
                <th className="border border-black p-1 text-left w-8">#</th>
                <th className="border border-black p-1 text-left">Tracking / PRO</th>
                <th className="border border-black p-1 text-left">Order</th>
                <th className="border border-black p-1 text-left">Ship To</th>
                <th className="border border-black p-1 text-left">Service</th>
                <th className="border border-black p-1 text-right">Pkg</th>
                <th className="border border-black p-1 text-right">Plt</th>
                <th className="border border-black p-1 text-right">Wt (lb)</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="border border-black p-3 text-center text-neutral-600">
                    No shipments on this manifest.
                  </td>
                </tr>
              ) : rows.map((r, i) => (
                <tr key={r.shipment_id} className="text-[11px]">
                  <td className="border border-black p-1 text-right">{i + 1}</td>
                  <td className="border border-black p-1 font-mono">{r.tracking_number ?? r.pro_number ?? "—"}</td>
                  <td className="border border-black p-1">{r.order_number ?? "—"}</td>
                  <td className="border border-black p-1">
                    <div>{r.ship_to_name ?? "—"}</div>
                    <div className="text-neutral-600 text-[10px]">
                      {[r.ship_to_city, r.ship_to_state, r.ship_to_postal].filter(Boolean).join(", ") || "—"}
                    </div>
                  </td>
                  <td className="border border-black p-1 uppercase">
                    {r.service ?? r.freight_mode ?? "—"}
                  </td>
                  <td className="border border-black p-1 text-right">{r.packages}</td>
                  <td className="border border-black p-1 text-right">{r.pallets}</td>
                  <td className="border border-black p-1 text-right">{r.weight_lb}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-neutral-50 font-semibold">
                <td className="border border-black p-1 text-right" colSpan={5}>Totals</td>
                <td className="border border-black p-1 text-right">{totals.packages}</td>
                <td className="border border-black p-1 text-right">{totals.pallets}</td>
                <td className="border border-black p-1 text-right">{totals.weight_lb}</td>
              </tr>
            </tfoot>
          </table>

          {/* Signatures */}
          <div className="grid grid-cols-2 gap-0 border border-black mt-4">
            <Sign title="Shipper Signature / Date" />
            <Sign title="Driver Signature / Pickup Date / Time" bordered />
          </div>

          <div className="flex justify-between text-[9px] text-neutral-500 mt-3">
            <span>{data.tenant?.name ?? ""}</span>
            <span>Printed {new Date().toLocaleString()} · {m.closed_at ? `Closed ${new Date(m.closed_at).toLocaleString()}` : "Not yet closed"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Total({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-neutral-600">{label}</div>
      <div className="text-lg font-bold leading-none">{value}</div>
    </div>
  );
}

function Sign({ title, bordered }: { title: string; bordered?: boolean }) {
  return (
    <div className={`p-2 h-20 ${bordered ? "border-l border-black" : ""}`}>
      <div className="text-[9px] uppercase tracking-wider text-neutral-600">{title}</div>
      <div className="border-b border-black mt-8" />
    </div>
  );
}